/**
 * Trains Page Script
 * Handles train search functionality and page interactions
 */

import { TrainSearchComponent } from './components/TrainSearchComponent';
import { TrainResultsComponent } from './components/TrainResultsComponent';
import { TrainFiltersComponent } from './components/TrainFiltersComponent';
import { TrainSearchData, TrainResult, TrainFilters } from './types/data-models';

class TrainsPage {
  private searchComponent!: TrainSearchComponent;
  private resultsComponent!: TrainResultsComponent;
  private filtersComponent!: TrainFiltersComponent;
  private mockResults: TrainResult[] = [];

  constructor() {
    this.initializeComponents();
    this.generateMockData();
    this.bindEvents();
  }

  private initializeComponents(): void {
    // Initialize search component
    const searchForm = document.querySelector('.train-search-form') as HTMLFormElement;
    if (searchForm) {
      this.searchComponent = new TrainSearchComponent(searchForm);
      this.searchComponent.onSearch = (data: TrainSearchData) => this.handleSearch(data);
    }

    // Initialize results component
    const resultsContainer = document.querySelector('.train-results') as HTMLElement;
    if (resultsContainer) {
      this.resultsComponent = new TrainResultsComponent(resultsContainer);
    }

    // Initialize filters component
    const filtersContainer = document.querySelector('.train-filters') as HTMLElement;
    if (filtersContainer) {
      this.filtersComponent = new TrainFiltersComponent(filtersContainer);
      this.filtersComponent.onFiltersChange = (filters: TrainFilters) => this.handleFiltersChange(filters);
    }
  }

  private generateMockData(): void {
    // Generate mock train results for demonstration
    this.mockResults = [
      {
        id: 'train-1',
        operator: 'SNCF Connect',
        operatorLogo: '../assets/images/train-operators/sncf-logo-placeholder.svg',
        trainNumber: 'TGV 6651',
        origin: 'Paris Gare de Lyon',
        destination: 'Lyon Part-Dieu',
        departureTime: new Date('2024-12-25T08:30:00'),
        arrivalTime: new Date('2024-12-25T10:30:00'),
        duration: '2h 00min',
        price: { min: 45, max: 120, currency: 'EUR' },
        class: '2ème classe',
        stops: [],
        platform: '3',
        amenities: ['WiFi gratuit', 'Prise électrique', 'Bar'],
        seatTypes: ['Siège standard', 'Siège confort']
      },
      {
        id: 'train-2',
        operator: 'SNCF Connect',
        operatorLogo: '../assets/images/train-operators/sncf-logo-placeholder.svg',
        trainNumber: 'TER 17234',
        origin: 'Paris Gare de Lyon',
        destination: 'Lyon Part-Dieu',
        departureTime: new Date('2024-12-25T10:15:00'),
        arrivalTime: new Date('2024-12-25T14:45:00'),
        duration: '4h 30min',
        price: { min: 25, max: 45, currency: 'EUR' },
        class: '2ème classe',
        stops: ['Melun', 'Fontainebleau', 'Sens', 'Joigny', 'Auxerre', 'Avallon', 'Montbard', 'Dijon', 'Beaune', 'Chalon-sur-Saône', 'Mâcon'],
        amenities: ['WiFi gratuit'],
        seatTypes: ['Siège standard']
      },
      {
        id: 'train-3',
        operator: 'SNCF Connect',
        operatorLogo: '../assets/images/train-operators/sncf-logo-placeholder.svg',
        trainNumber: 'TGV 6653',
        origin: 'Paris Gare de Lyon',
        destination: 'Lyon Part-Dieu',
        departureTime: new Date('2024-12-25T12:30:00'),
        arrivalTime: new Date('2024-12-25T14:30:00'),
        duration: '2h 00min',
        price: { min: 55, max: 140, currency: 'EUR' },
        class: '1ère classe',
        stops: [],
        platform: '5',
        amenities: ['WiFi gratuit', 'Prise électrique', 'Bar', 'Repas inclus'],
        seatTypes: ['Siège confort', 'Siège business']
      },
      {
        id: 'train-4',
        operator: 'Ouigo',
        operatorLogo: '../assets/images/train-operators/ouigo-logo-placeholder.svg',
        trainNumber: 'OUIGO 7621',
        origin: 'Paris Gare de Lyon',
        destination: 'Lyon Part-Dieu',
        departureTime: new Date('2024-12-25T16:45:00'),
        arrivalTime: new Date('2024-12-25T19:15:00'),
        duration: '2h 30min',
        price: { min: 19, max: 35, currency: 'EUR' },
        class: 'Unique',
        stops: ['Mâcon Loché TGV'],
        amenities: ['WiFi payant'],
        seatTypes: ['Siège standard']
      }
    ];
  }

  private bindEvents(): void {
    // Listen for train selection events
    document.addEventListener('trainSelected', ((event: CustomEvent) => {
      this.handleTrainSelection(event.detail.train);
    }) as EventListener);

    // Handle mobile filters toggle
    const filtersToggle = document.querySelector('.train-page__filters-toggle') as HTMLButtonElement;
    const filtersPanel = document.querySelector('.train-filters') as HTMLElement;
    
    if (filtersToggle && filtersPanel) {
      filtersToggle.addEventListener('click', () => {
        const isExpanded = filtersToggle.getAttribute('aria-expanded') === 'true';
        filtersToggle.setAttribute('aria-expanded', (!isExpanded).toString());
        filtersPanel.classList.toggle('train-filters--mobile-open', !isExpanded);
      });
    }
  }

  private async handleSearch(searchData: TrainSearchData): Promise<void> {
    console.log('Searching trains with data:', searchData);
    
    // Show loading state
    this.resultsComponent.showLoading();
    
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // Filter mock results based on search criteria
    const filteredResults = this.filterResultsBySearch(searchData);
    
    // Display results
    this.resultsComponent.showResults(filteredResults);
    
    // Update page state
    this.updatePageState(searchData, filteredResults);
  }

  private filterResultsBySearch(searchData: TrainSearchData): TrainResult[] {
    // In a real application, this would be an API call
    // For now, we'll filter our mock data based on the search criteria
    
    return this.mockResults.filter(result => {
      // Simple filtering logic for demonstration
      const matchesOrigin = result.origin.toLowerCase().includes(searchData.origin.toLowerCase());
      const matchesDestination = result.destination.toLowerCase().includes(searchData.destination.toLowerCase());
      
      return matchesOrigin && matchesDestination;
    });
  }

  private handleFiltersChange(filters: TrainFilters): void {
    console.log('Filters changed:', filters);
    
    // Apply filters to current results
    const filteredResults = this.applyFilters(this.mockResults, filters);
    this.resultsComponent.showResults(filteredResults);
  }

  private applyFilters(results: TrainResult[], filters: TrainFilters): TrainResult[] {
    return results.filter(result => {
      // Price range filter
      if (filters.priceRange) {
        if (result.price.min < filters.priceRange.min || result.price.min > filters.priceRange.max) {
          return false;
        }
      }

      // Operators filter
      if (filters.operators && filters.operators.length > 0) {
        if (!filters.operators.includes(result.operator)) {
          return false;
        }
      }

      // Departure time ranges filter
      if (filters.departureTimeRanges && filters.departureTimeRanges.length > 0) {
        const hour = result.departureTime.getHours();
        const matchesTimeRange = filters.departureTimeRanges.some(range => {
          switch (range) {
            case 'morning': return hour >= 6 && hour < 12;
            case 'afternoon': return hour >= 12 && hour < 18;
            case 'evening': return hour >= 18 && hour < 24;
            case 'night': return hour >= 0 && hour < 6;
            default: return true;
          }
        });
        if (!matchesTimeRange) return false;
      }

      // Duration filter
      if (filters.duration) {
        const durationMinutes = this.parseDurationToMinutes(result.duration);
        if (durationMinutes < filters.duration.min || durationMinutes > filters.duration.max) {
          return false;
        }
      }

      // Class filter
      if (filters.class && filters.class.length > 0) {
        if (!filters.class.includes(result.class)) {
          return false;
        }
      }

      return true;
    });
  }

  private parseDurationToMinutes(duration: string): number {
    // Parse duration string like "2h 30min" to minutes
    const hoursMatch = duration.match(/(\d+)h/);
    const minutesMatch = duration.match(/(\d+)min/);
    
    const hours = hoursMatch && hoursMatch[1] ? parseInt(hoursMatch[1]) : 0;
    const minutes = minutesMatch && minutesMatch[1] ? parseInt(minutesMatch[1]) : 0;
    
    return hours * 60 + minutes;
  }

  private handleTrainSelection(train: TrainResult): void {
    console.log('Train selected:', train);
    
    // In a real application, this would navigate to booking page
    alert(`Train sélectionné: ${train.trainNumber} - ${train.operator}\nPrix: €${train.price.min}\nDépart: ${train.departureTime.toLocaleString('fr-FR')}`);
  }

  private updatePageState(searchData: TrainSearchData, results: TrainResult[]): void {
    // Update page title with search info
    const pageTitle = document.querySelector('.train-page__title') as HTMLElement;
    if (pageTitle) {
      pageTitle.textContent = `Trains de ${searchData.origin} à ${searchData.destination}`;
    }

    // Update results count
    const resultsCount = document.querySelector('.train-results__count') as HTMLElement;
    if (resultsCount) {
      const count = results.length;
      resultsCount.textContent = `${count} train${count !== 1 ? 's' : ''} trouvé${count !== 1 ? 's' : ''}`;
    }

    // Show/hide filters panel based on results
    const filtersPanel = document.querySelector('.train-filters') as HTMLElement;
    if (filtersPanel) {
      filtersPanel.style.display = results.length > 0 ? 'block' : 'none';
    }
  }
}

// Initialize the trains page when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new TrainsPage();
});